(() => {
  const SECTION_SELECTOR = '[data-featured-products-manual]';

  const initSection = (section) => {
    if (!section || section.dataset.featuredProductsInitialized === 'true') return;

    const tabs = Array.from(section.querySelectorAll('[role="tab"]'));
    const panels = Array.from(section.querySelectorAll('[role="tabpanel"]'));
    const previousButton = section.querySelector('[data-carousel-previous]');
    const nextButton = section.querySelector('[data-carousel-next]');

    if (!tabs.length || !panels.length) return;

    section.dataset.featuredProductsInitialized = 'true';

    const getPanelForTab = (tab) => {
      const panelId = tab.getAttribute('aria-controls');
      return panels.find((panel) => panel.id === panelId) || null;
    };

    const getActiveTab = () => tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    const getActivePanel = () => getPanelForTab(getActiveTab());
    const getActiveTrack = () => getActivePanel()?.querySelector('[data-product-track]') || null;

    const updateArrowState = () => {
      const track = getActiveTrack();

      if (!previousButton || !nextButton) return;
      if (!track) {
        previousButton.disabled = true;
        nextButton.disabled = true;
        return;
      }

      const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      previousButton.disabled = track.scrollLeft <= 2;
      nextButton.disabled = maximumScroll <= 2 || track.scrollLeft >= maximumScroll - 2;
    };

    const resetPanelTrack = (panel) => {
      const track = panel?.querySelector('[data-product-track]');
      if (!track) return;
      track.scrollTo({ left: 0, behavior: 'auto' });
    };

    const activateTab = (tab, options = {}) => {
      if (!tab || !tabs.includes(tab)) return;

      const { focus = false, reset = true } = options;
      tabs.forEach((candidate) => {
        const isActive = candidate === tab;
        candidate.classList.toggle('is-active', isActive);
        candidate.setAttribute('aria-selected', String(isActive));
        candidate.setAttribute('tabindex', isActive ? '0' : '-1');

        const panel = getPanelForTab(candidate);
        if (panel) panel.hidden = !isActive;
      });

      const activePanel = getPanelForTab(tab);
      if (reset) resetPanelTrack(activePanel);
      if (focus) tab.focus();
      tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      window.requestAnimationFrame(updateArrowState);
    };

    const moveToTab = (currentTab, targetIndex) => {
      const normalizedIndex = (targetIndex + tabs.length) % tabs.length;
      activateTab(tabs[normalizedIndex], { focus: true });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activateTab(tab));
      tab.addEventListener('keydown', (event) => {
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            moveToTab(tab, index - 1);
            break;
          case 'ArrowRight':
            event.preventDefault();
            moveToTab(tab, index + 1);
            break;
          case 'Home':
            event.preventDefault();
            moveToTab(tab, 0);
            break;
          case 'End':
            event.preventDefault();
            moveToTab(tab, tabs.length - 1);
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            activateTab(tab, { focus: true });
            break;
        }
      });
    });

    const scrollActiveTrack = (direction) => {
      const track = getActiveTrack();
      const firstCard = track?.querySelector('[data-product-card]');
      if (!track || !firstCard) return;

      const computedStyle = window.getComputedStyle(track);
      const gap = Number.parseFloat(computedStyle.columnGap || computedStyle.gap) || 0;
      const distance = firstCard.getBoundingClientRect().width + gap;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      track.scrollBy({
        left: distance * direction,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    };

    previousButton?.addEventListener('click', () => scrollActiveTrack(-1));
    nextButton?.addEventListener('click', () => scrollActiveTrack(1));

    panels.forEach((panel) => {
      const track = panel.querySelector('[data-product-track]');
      if (!track) return;

      let scrollFrame;
      track.addEventListener(
        'scroll',
        () => {
          window.cancelAnimationFrame(scrollFrame);
          scrollFrame = window.requestAnimationFrame(updateArrowState);
        },
        { passive: true }
      );
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(updateArrowState);
      panels.forEach((panel) => {
        const track = panel.querySelector('[data-product-track]');
        if (track) resizeObserver.observe(track);
      });
      section.featuredProductsResizeObserver = resizeObserver;
    } else {
      window.addEventListener('resize', updateArrowState);
    }

    section.featuredProductsManual = {
      activateByKey: (key) => {
        const tab = tabs.find((candidate) => candidate.dataset.tabKey === key);
        activateTab(tab);
      },
    };

    activateTab(getActiveTab(), { reset: false });
  };

  const initAll = (root = document) => {
    if (root.matches?.(SECTION_SELECTOR)) initSection(root);
    root.querySelectorAll?.(SECTION_SELECTOR).forEach(initSection);
  };

  const handleBlockSelect = (event) => {
    const card = event.target.closest?.('[data-product-card]');
    const section = card?.closest(SECTION_SELECTOR);
    if (!card || !section) return;

    initSection(section);
    section.featuredProductsManual?.activateByKey(card.dataset.productCategory);
    card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', handleBlockSelect);
})();
